import { Component, Input, Output, EventEmitter, ChangeDetectorRef, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { TranslateService } from '@ngx-translate/core';
import {
  SimpleFilter,
  CompoundFilter,
  PredicateFilter,
  isSimpleFilter,
  isCompoundFilter,
  predicateOperatorOptions,
  deepCopy,
  isDate,
  isDateTime
} from 'na-lib';
import { WorkTaskTemplateFilterDialogComponent } from '../work-task-template-filter-dialog/work-task-template-filter-dialog.component';

@Component({
    selector: 'app-work-task-template-filters',
    templateUrl: './work-task-template-filters.component.html',
    styleUrls: ['./work-task-template-filters.component.css'],
    standalone: false
})
export class WorkTaskTemplateFiltersComponent {
  @Input() showHeader = true
  @Input() requireValue = false
  @Input() set predicates(value: PredicateFilter[]) {
    value?.forEach( (v:any, index) => {
      if(typeof v === "string") {
        const item:any = v
        const fields = item?.split(" ")
        value[index] = {
          field: fields[0],
          operator: fields[1],
          value: fields[2]
        }
      }
    })
    this._predicates = value || []
    this.setDataSource(this._predicates)
  }
  @Input() disabled = false
  @Output() predicatesChange = new EventEmitter<PredicateFilter[]>()

  _predicates: PredicateFilter[] = []
  operatorOptions = predicateOperatorOptions

  treeControl = new NestedTreeControl<PredicateFilter>(node => this.getChildren(node));
  dataSource = new MatTreeNestedDataSource<PredicateFilter>();

  filterTypes = [
    { type: '=', translation: 'predicateFilter.addComparison', name: 'comparison' },
    { type: 'and', translation: 'predicateFilter.addAndGroup', name: 'and' },
    { type: 'or', translation: 'predicateFilter.addOrGroup', name: 'or' }
  ]

  constructor(
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
    private translate: TranslateService
  ) {}

  // --- Tree helpers ---

  getChildren(node: PredicateFilter): PredicateFilter[] {
    if (isCompoundFilter(node)) {
      return node.and || node.or || []
    }
    return []
  }

  hasGroup = (_: number, node: PredicateFilter) =>
    isCompoundFilter(node) && (Array.isArray((node as CompoundFilter).and) || Array.isArray((node as CompoundFilter).or));

  isComparison = (_: number, node: PredicateFilter) => isSimpleFilter(node);

  // --- Add ---

  addFilter(type: string) {
    switch (type) {
      case '=': {
        this.openSimpleFilterDialog(null, (result) => {
          this._predicates = [...this._predicates, result]
          this.emitAndRefresh()
        })
        break
      }
      case 'and': {
        this._predicates = [...this._predicates, { and: [] } as CompoundFilter]
        this.emitAndRefresh()
        break
      }
      case 'or': {
        this._predicates = [...this._predicates, { or: [] } as CompoundFilter]
        this.emitAndRefresh()
        break
      }
    }
  }

  addFilterToNode(type: string, node: CompoundFilter) {
    const groupKey = node.and ? 'and' : 'or'
    switch (type) {
      case '=': {
        this.openSimpleFilterDialog(null, (result) => {
          node[groupKey] = [...(node[groupKey] || []), result]
          this.emitAndRefresh()
        })
        break
      }
      case 'and': {
        node[groupKey] = [...(node[groupKey] || []), { and: [] }]
        this.emitAndRefresh()
        break
      }
      case 'or': {
        node[groupKey] = [...(node[groupKey] || []), { or: [] }]
        this.emitAndRefresh()
        break
      }
    }
  }

  // --- Edit ---

  editNode(node: PredicateFilter) {
    if (isSimpleFilter(node)) {
      this.openSimpleFilterDialog(node, (result) => {
        Object.keys(node).forEach(key => delete (node as any)[key])
        Object.assign(node, result)
        this.emitAndRefresh()
      })
    }
  }

  toggleGroupKind(node: CompoundFilter, to: 'and' | 'or') {
    if (to === 'and' && node.or) {
      node.and = [...node.or]
      delete node.or
    } else if (to === 'or' && node.and) {
      node.or = [...node.and]
      delete node.and
    }
    this.emitAndRefresh()
  }

  // --- Delete ---

  deleteNode(node: PredicateFilter) {
    const idx = this._predicates.indexOf(node)
    if (idx > -1) {
      this._predicates = this._predicates.filter((_, i) => i !== idx)
      this.emitAndRefresh(true)
      return
    }
    for (const root of this._predicates) {
      if (this.removeFromParent(root, node)) {
        this.emitAndRefresh(true)
        return
      }
    }
  }

  deleteAll() {
    this._predicates = []
    this.emitAndRefresh(true)
  }

  private removeFromParent(parent: PredicateFilter, target: PredicateFilter): boolean {
    if (!isCompoundFilter(parent)) return false
    const key = (parent as CompoundFilter).and ? 'and' : 'or'
    const arr = (parent as CompoundFilter)[key]
    if (!arr) return false
    const idx = arr.indexOf(target)
    if (idx > -1) {
      (parent as CompoundFilter)[key] = arr.filter((_, i) => i !== idx)
      return true
    }
    for (const child of arr) {
      if (this.removeFromParent(child, target)) return true
    }
    return false
  }

  private removeEmptyGroups(predicates: PredicateFilter[]): PredicateFilter[] {
    return predicates.reduce((cleaned: PredicateFilter[], predicate: PredicateFilter) => {
      if (isCompoundFilter(predicate)) {
        const key = (predicate as CompoundFilter).and ? 'and' : 'or'
        const children = this.removeEmptyGroups(((predicate as CompoundFilter)[key] || []) as PredicateFilter[])

        if (children.length > 0) {
          cleaned.push({ [key]: children } as CompoundFilter)
        }

        return cleaned
      }

      cleaned.push(predicate)
      return cleaned
    }, [])
  }

  // --- Display helpers ---

  formatFilterValue(filter: any): string {
    if (filter.type === 'date') return isDate(filter.value)
    if (filter.type === 'dateTime') return isDateTime(filter.value)
    return filter.value !== undefined && filter.value !== null ? filter.value : ''
  }

  getOperatorText(operator: string): string {
    const op = this.operatorOptions.find(o => o.value === operator)
    return op ? op.translation : operator
  }

  // --- Private ---

  private openSimpleFilterDialog(filter: SimpleFilter | null, onSave: (result: SimpleFilter) => void) {
    let dialogRef = this.dialog.open(WorkTaskTemplateFilterDialogComponent, {
      data: { filter: filter, requireValue: this.requireValue },
      width: '600px'
    })
    dialogRef.afterClosed().subscribe(result => {
      if (result && result[0]) {
        onSave(result[1])
      }
    })
  }

  private emitAndRefresh(removeEmptyGroups = false) {
    if (removeEmptyGroups) {
      this._predicates = this.removeEmptyGroups(this._predicates)
    }
    this.predicatesChange.emit(this._predicates)
    this.refreshTree()
  }

  private setDataSource(predicates: PredicateFilter[]) {
    this.dataSource.data = predicates
    this.treeControl.dataNodes = predicates as any
    this.treeControl.expandAll()
  }

  private refreshTree() {
    const clone = deepCopy(this._predicates)
    this._predicates = clone
    this.dataSource.data = clone
    this.treeControl.dataNodes = clone as any
    this.zone.run(() => this.cdr.detectChanges())
    this.treeControl.expandAll()
  }
}
